import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, Checkbox, Drawer, Dropdown, Menu, Space, Table } from "antd";
import { DownloadOutlined, EyeOutlined, FilterOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { useTranslation } from "react-i18next";
import { formatPrice, formatWeight } from "../../../../functions/formatters";
import { CURRENCY } from "../../../../constants/DeliveryConstants";
import utils from "utils";
import _ from "lodash";
import EllipsisDropdown from "../../../../components/shared-components/EllipsisDropdown";
import Flex from "../../../../components/shared-components/Flex";
import dayjs from "dayjs";
import { useProducts } from "../../../../data/hooks/firestore/useProducts";
import { useStockReport } from "../../../../data/hooks/firestore/useStockReport";
import { calculateOrderCredits, calculateOrderedQuantity, expandCartBundles } from "../../../../functions/reports";
import { productsAvailabilityReport } from "../../../../urls";
import { Link } from "react-router-dom";
import styled from "styled-components";
import useReportFilters from "../../../../data/hooks/url-parameters/useReportFilters";

const ProductsTable = React.memo(({ columns, dataSource, loading }) => {
  return (
    <div className="table-responsive">
      <Table
        showSorterTooltip={false}
        columns={columns}
        dataSource={dataSource}
        rowKey="productId"
        loading={loading}
        pagination={{ showSizeChanger: true }}
        scroll={{ x: 600 }}
      />
    </div>
  );
});

const ProductsReport = ({ orders = [], invoices = [], dateRange, dateRangeText, loading, companyFilter = "All" }) => {
  const { t } = useTranslation();
  const { products } = useProducts();
  const { getAggregatedAddedStock } = useStockReport(dateRange);
  const { getURLParamsString } = useReportFilters();
  const [productsData, setProductsData] = useState([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProductCustomers, setSelectedProductCustomers] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedProductGroups, setSelectedProductGroups] = useState([]);
  const [pendingSelectedCategories, setPendingSelectedCategories] = useState([]);
  const [pendingSelectedProductGroups, setPendingSelectedProductGroups] = useState([]);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [productGroupDropdownOpen, setProductGroupDropdownOpen] = useState(false);
  const [categoryPendingInitialized, setCategoryPendingInitialized] = useState(false);
  const [productGroupPendingInitialized, setProductGroupPendingInitialized] = useState(false);

  const productsById = useMemo(() => {
    const map = new Map();
    (products || []).forEach((p) => {
      map.set(p.id, p);
    });
    return map;
  }, [products]);

  const getProductDetails = useCallback(
    (productId) => {
      return productsById.get(productId);
    },
    [productsById],
  );

  const getProductCategoryForFilters = useCallback(
    (productId, productCategoryFallback) => {
      const productDetails = getProductDetails(productId);
      return productDetails?.category?.en || productCategoryFallback;
    },
    [getProductDetails],
  );

  // Excel export function
  const handleExportToExcel = () => {
    const exportData = filteredProductsData.map((product, index) => ({
      [t("rank")]: index + 1,
      [t("product_name")]: product.productName,
      [t("ordered_quantity")]: product.totalOrderedQuantity,
      [t("delivered_quantity")]: product.totalDeliveredQuantity,
      [t("packed_units")]: product.totalPackedQuantity,
      [t("credited_quantity")]: product.totalCreditedQuantity,
      [`${t("total_weight")} (g)`]: product.totalWeight,
      [t("total_revenue")]: Number(product.totalValue || 0),
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);

    // Ensure revenue column is treated as a number in Excel (not locale-formatted text)
    const revenueHeader = t("total_revenue");
    const range = XLSX.utils.decode_range(worksheet["!ref"]);
    let revenueCol = null;

    for (let c = range.s.c; c <= range.e.c; c++) {
      const cellAddress = XLSX.utils.encode_cell({ r: range.s.r, c });
      const cell = worksheet[cellAddress];
      if (cell?.v === revenueHeader) {
        revenueCol = c;
        break;
      }
    }

    if (revenueCol !== null) {
      for (let r = range.s.r + 1; r <= range.e.r; r++) {
        const cellAddress = XLSX.utils.encode_cell({ r, c: revenueCol });
        const cell = worksheet[cellAddress];
        if (!cell) continue;
        cell.t = "n";
        cell.v = Number(cell.v || 0);
        cell.z = "#,##0.00";
      }
    }
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, t("products_report"));

    const fileName = `${t("products_report")}_${dateRangeText.replace(/\s+/g, "_")}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  // Get packed products data
  const packedProductsData = useMemo(() => {
    const aggregatedStock = getAggregatedAddedStock(companyFilter, products);
    const packedMap = {};
    aggregatedStock.forEach((item) => {
      packedMap[item.productId] = item.totalAddedStock;
    });
    return packedMap;
  }, [getAggregatedAddedStock, companyFilter, products]);

  // Process orders data into products data with proper date filtering
  useEffect(() => {
    if (orders && orders.length > 0 && dateRange && products) {
      // Apply date filtering
      const start = parseInt(dayjs(dateRange[0]).startOf("day").format("x"));
      const end = parseInt(dayjs(dateRange[1]).endOf("day").format("x"));

      const filteredByDate = orders.filter((order) => {
        const deliveryDate = dayjs(order.farmorderDeliveryDate).format("DD-MM-YYYY");
        const orderTimestamp = parseInt(dayjs(deliveryDate, "DD-MM-YYYY").format("x"));
        return orderTimestamp >= start && orderTimestamp <= end;
      });

      // Collect all products from all orders
      const allProducts = {};

      filteredByDate.forEach((order) => {
        if (order.userShoppingCart) {
          // Calculate credits for this order
          const orderCredits = calculateOrderCredits(order, invoices, filteredByDate, products);

          // Expand bundles
          const expandedCart = expandCartBundles(order.userShoppingCart, products);

          expandedCart.forEach((product) => {
            const productId = product.productId;
            const productDetails = getProductDetails(productId);
            const productWeight = productDetails?.weight || 0;
            const productGroup = productDetails?.productGroup;
            const productCategoryForFilters = getProductCategoryForFilters(productId, product.productCategory);

            // Calculate ordered quantity for this product in this order
            const orderedQty = calculateOrderedQuantity(order, productId, products);
            const deliveredQty = product.itemCount;

            // Check if this product was credited
            const creditedProduct = orderCredits.creditedProducts.find((cp) => cp.productId === productId);
            const creditedQty = creditedProduct ? creditedProduct.quantity : 0;
            const creditedValue = creditedQty * product.productPrice;

            if (allProducts[productId]) {
              allProducts[productId].totalOrderedQuantity += orderedQty;
              allProducts[productId].totalDeliveredQuantity += deliveredQty;
              allProducts[productId].totalCreditedQuantity += creditedQty;
              allProducts[productId].totalValue += product.productPrice * deliveredQty - creditedValue;
              allProducts[productId].totalWeight += deliveredQty * productWeight;
              allProducts[productId].orders.push({
                order,
                orderedQuantity: orderedQty,
                deliveredQuantity: deliveredQty,
                creditedQuantity: creditedQty,
                value: product.productPrice * deliveredQty - creditedValue,
                weight: deliveredQty * productWeight,
              });
            } else {
              allProducts[productId] = {
                productId,
                productName: product.productName,
                productCategory: product.productCategory,
                productCategoryForFilters,
                productGroup,
                totalOrderedQuantity: orderedQty,
                totalDeliveredQuantity: deliveredQty,
                totalCreditedQuantity: creditedQty,
                totalValue: product.productPrice * deliveredQty - creditedValue,
                totalWeight: deliveredQty * productWeight,
                orders: [
                  {
                    order,
                    orderedQuantity: orderedQty,
                    deliveredQuantity: deliveredQty,
                    creditedQuantity: creditedQty,
                    value: product.productPrice * deliveredQty - creditedValue,
                    weight: deliveredQty * productWeight,
                  },
                ],
              };
            }
          });
        }
      });

      // Process standalone credit invoices (not linked to orders)
      const standaloneCredits = invoices.filter(
        (invoice) =>
          invoice.invoiceType === "credit" &&
          (!invoice.farmorderIds || invoice.farmorderIds.length === 0) &&
          invoice.totalAmount &&
          invoice.totalAmount < 0,
      );

      standaloneCredits.forEach((creditInvoice) => {
        const creditDate = dayjs(creditInvoice.invoiceDate).format("DD-MM-YYYY");
        const creditTimestamp = parseInt(dayjs(creditDate, "DD-MM-YYYY").format("x"));

        // Only process credits in the current period
        if (creditTimestamp >= start && creditTimestamp <= end) {
          // Expand shopping cart to handle bundles
          const expandedCart = expandCartBundles(creditInvoice.userShoppingCart || [], products);

          expandedCart.forEach((product) => {
            const productId = product.productId;
            const productDetails = getProductDetails(productId);
            const productWeight = productDetails?.weight || 0;
            const productGroup = productDetails?.productGroup;
            const productCategoryForFilters = getProductCategoryForFilters(productId, product.productCategory);

            // Credit invoices have negative itemCount
            const creditedQty = Math.abs(product.itemCount);
            const paymentPrice =
              typeof product.productPrice === "string" ? parseFloat(product.productPrice) : product.productPrice;
            const creditedValue = creditedQty * paymentPrice;

            if (allProducts[productId]) {
              // Subtract from totals
              allProducts[productId].totalCreditedQuantity += creditedQty;
              allProducts[productId].totalValue -= creditedValue;
              allProducts[productId].totalWeight -= creditedQty * productWeight;
            } else {
              // Create entry for credited product even if not sold in period
              allProducts[productId] = {
                productId,
                productName: product.productName,
                productCategory: product.productCategory,
                productCategoryForFilters,
                productGroup,
                totalOrderedQuantity: 0,
                totalDeliveredQuantity: 0,
                totalCreditedQuantity: creditedQty,
                totalValue: -creditedValue,
                totalWeight: -creditedQty * productWeight,
                orders: [],
              };
            }
          });
        }
      });

      // Add packed quantities to all products after aggregation
      Object.keys(allProducts).forEach((productId) => {
        allProducts[productId].totalPackedQuantity = packedProductsData[productId] || 0;
      });

      const productsArray = Object.values(allProducts);
      // Sort by total delivered quantity (highest first)
      const sortedProducts = _.orderBy(productsArray, ["totalDeliveredQuantity"], ["desc"]);

      setProductsData((prev) => {
        if (!Array.isArray(prev) || prev.length !== sortedProducts.length) return sortedProducts;
        for (let i = 0; i < sortedProducts.length; i++) {
          const a = prev[i];
          const b = sortedProducts[i];
          if (!a || !b) return sortedProducts;
          if (a.productId !== b.productId) return sortedProducts;
          if (a.totalOrderedQuantity !== b.totalOrderedQuantity) return sortedProducts;
          if (a.totalDeliveredQuantity !== b.totalDeliveredQuantity) return sortedProducts;
          if (a.totalPackedQuantity !== b.totalPackedQuantity) return sortedProducts;
          if (a.totalCreditedQuantity !== b.totalCreditedQuantity) return sortedProducts;
          if (a.totalValue !== b.totalValue) return sortedProducts;
          if (a.totalWeight !== b.totalWeight) return sortedProducts;
        }
        return prev;
      });
    } else {
      setProductsData((prev) => (Array.isArray(prev) && prev.length === 0 ? prev : []));
    }
  }, [orders, invoices, dateRange, products, getProductCategoryForFilters, getProductDetails, packedProductsData]);

  const categoryOptions = useMemo(() => {
    const categories = productsData.map((p) => p.productCategoryForFilters).filter(Boolean);
    return _.sortBy(_.uniq(categories));
  }, [productsData]);

  const productGroupOptions = useMemo(() => {
    const groups = productsData.map((p) => p.productGroup).filter(Boolean);
    return _.sortBy(_.uniq(groups));
  }, [productsData]);

  const filteredProductsData = useMemo(() => {
    return productsData.filter((product) => {
      const categoryMatch =
        selectedCategories.length === 0 || selectedCategories.includes(product.productCategoryForFilters);
      const groupMatch = selectedProductGroups.length === 0 || selectedProductGroups.includes(product.productGroup);
      return categoryMatch && groupMatch;
    });
  }, [productsData, selectedCategories, selectedProductGroups]);

  const isCategoryFilterActive = selectedCategories.length > 0;
  const isProductGroupFilterActive = selectedProductGroups.length > 0;

  const handleResetCategoryFilter = () => {
    setSelectedCategories([]);
    setPendingSelectedCategories([]);
    setCategoryDropdownOpen((prev) => (prev ? false : prev));
  };

  const handleConfirmCategoryFilter = () => {
    setSelectedCategories(pendingSelectedCategories);
    setCategoryDropdownOpen((prev) => (prev ? false : prev));
  };

  const handleCategoryDropdownOpenChange = (nextOpen) => {
    setCategoryDropdownOpen((prev) => (prev === nextOpen ? prev : nextOpen));
    if (nextOpen) {
      setCategoryPendingInitialized(false);
    }
  };

  const handleResetProductGroupFilter = () => {
    setSelectedProductGroups([]);
    setPendingSelectedProductGroups([]);
    setProductGroupDropdownOpen((prev) => (prev ? false : prev));
  };

  const handleConfirmProductGroupFilter = () => {
    setSelectedProductGroups(pendingSelectedProductGroups);
    setProductGroupDropdownOpen((prev) => (prev ? false : prev));
  };

  const handleProductGroupDropdownOpenChange = (nextOpen) => {
    setProductGroupDropdownOpen((prev) => (prev === nextOpen ? prev : nextOpen));
    if (nextOpen) {
      setProductGroupPendingInitialized(false);
    }
  };

  useEffect(() => {
    if (!categoryDropdownOpen) {
      if (categoryPendingInitialized) setCategoryPendingInitialized(false);
      return;
    }

    if (!categoryPendingInitialized) {
      setPendingSelectedCategories(selectedCategories);
      setCategoryPendingInitialized(true);
    }
  }, [categoryDropdownOpen, categoryPendingInitialized, selectedCategories]);

  useEffect(() => {
    if (!productGroupDropdownOpen) {
      if (productGroupPendingInitialized) setProductGroupPendingInitialized(false);
      return;
    }

    if (!productGroupPendingInitialized) {
      setPendingSelectedProductGroups(selectedProductGroups);
      setProductGroupPendingInitialized(true);
    }
  }, [productGroupDropdownOpen, productGroupPendingInitialized, selectedProductGroups]);

  const showProductDetails = useCallback(
    (product) => {
      // Group orders by customer and calculate totals for this specific product
      const customerGroups = {};

      product.orders.forEach(({ order, orderedQuantity, deliveredQuantity, creditedQuantity, value, weight }) => {
        const customerId = order.farmorderCustomerId;
        const customerName = order.farmorderCustomerName;

        if (customerGroups[customerId]) {
          customerGroups[customerId].totalOrderedQuantity += orderedQuantity;
          customerGroups[customerId].totalDeliveredQuantity += deliveredQuantity;
          customerGroups[customerId].totalCreditedQuantity += creditedQuantity || 0;
          customerGroups[customerId].totalValue += value;
          customerGroups[customerId].totalWeight += weight;
        } else {
          customerGroups[customerId] = {
            customerId,
            customerName,
            totalOrderedQuantity: orderedQuantity,
            totalDeliveredQuantity: deliveredQuantity,
            totalCreditedQuantity: creditedQuantity || 0,
            totalValue: value,
            totalWeight: weight,
          };
        }
      });

      // Process standalone credit invoices for this product
      if (invoices && dateRange) {
        const start = parseInt(dayjs(dateRange[0]).startOf("day").format("x"));
        const end = parseInt(dayjs(dateRange[1]).endOf("day").format("x"));

        const standaloneCredits = invoices.filter(
          (invoice) =>
            invoice.invoiceType === "credit" &&
            (!invoice.farmorderIds || invoice.farmorderIds.length === 0) &&
            invoice.totalAmount &&
            invoice.totalAmount < 0,
        );

        standaloneCredits.forEach((creditInvoice) => {
          const creditDate = dayjs(creditInvoice.invoiceDate).format("DD-MM-YYYY");
          const creditTimestamp = parseInt(dayjs(creditDate, "DD-MM-YYYY").format("x"));

          // Only process credits in the current period
          if (creditTimestamp >= start && creditTimestamp <= end) {
            const expandedCart = expandCartBundles(creditInvoice.userShoppingCart || [], products);

            // Find this product in the credit invoice
            const creditedProduct = expandedCart.find((p) => p.productId === product.productId);

            if (creditedProduct) {
              const customerId = creditInvoice.farmorderCustomerId;
              const customerName = creditInvoice.farmorderCustomerName;
              const creditedQty = Math.abs(creditedProduct.itemCount);
              const paymentPrice =
                typeof creditedProduct.productPrice === "string"
                  ? parseFloat(creditedProduct.productPrice)
                  : creditedProduct.productPrice;
              const creditedValue = creditedQty * paymentPrice;

              const productDetails = getProductDetails(product.productId);
              const productWeight = productDetails?.weight || 0;

              if (customerGroups[customerId]) {
                customerGroups[customerId].totalCreditedQuantity += creditedQty;
                customerGroups[customerId].totalValue -= creditedValue;
                customerGroups[customerId].totalWeight -= creditedQty * productWeight;
              } else {
                // Create entry for customer with only credits
                customerGroups[customerId] = {
                  customerId,
                  customerName,
                  totalOrderedQuantity: 0,
                  totalDeliveredQuantity: 0,
                  totalCreditedQuantity: creditedQty,
                  totalValue: -creditedValue,
                  totalWeight: -creditedQty * productWeight,
                };
              }
            }
          }
        });
      }

      const customersArray = Object.values(customerGroups);
      // Sort by total delivered quantity (highest first)
      const sortedCustomers = _.orderBy(customersArray, ["totalDeliveredQuantity"], ["desc"]);

      setSelectedProduct(product);
      setSelectedProductCustomers(sortedCustomers);
      setDrawerVisible(true);
    },
    [invoices, dateRange, products, getProductDetails],
  );

  const dropdownMenu = useCallback(
    (product) => (
      <Menu>
        <Menu.Item>
          <a onClick={() => showProductDetails(product)}>
            <Flex alignItems="center">
              <EyeOutlined />
              <span style={{ marginLeft: 4 }}>{t("details")}</span>
            </Flex>
          </a>
        </Menu.Item>
      </Menu>
    ),
    [showProductDetails, t],
  );

  const closeDrawer = () => {
    setDrawerVisible(false);
    setTimeout(() => {
      setSelectedProduct(null);
      setSelectedProductCustomers([]);
    }, 300);
  };

  // Table columns for products
  const productsColumns = useMemo(
    () => [
      {
        title: t("product_name"),
        dataIndex: "productName",
        render: (productName, product) => (
          <button className={"text-button"} onClick={() => showProductDetails(product)}>
            <span style={{ display: "block" }}>{productName}</span>
          </button>
        ),
        sorter: (a, b) => utils.antdTableSorter(a, b, "productName"),
      },
      {
        title: t("ordered_quantity"),
        dataIndex: "totalOrderedQuantity",
        sorter: (a, b) => utils.antdTableSorter(a, b, "totalOrderedQuantity"),
      },
      {
        title: t("delivered_quantity"),
        dataIndex: "totalDeliveredQuantity",
        sorter: (a, b) => utils.antdTableSorter(a, b, "totalDeliveredQuantity"),
      },
      {
        title: t("packed_units"),
        dataIndex: "totalPackedQuantity",
        sorter: (a, b) => utils.antdTableSorter(a, b, "totalPackedQuantity"),
      },
      {
        title: t("credited_quantity"),
        dataIndex: "totalCreditedQuantity",
        render: (qty) => (qty > 0 ? <span style={{ color: "#ff4d4f" }}>-{qty}</span> : "-"),
        sorter: (a, b) => utils.antdTableSorter(a, b, "totalCreditedQuantity"),
      },
      {
        title: t("total_weight"),
        dataIndex: "totalWeight",
        render: (weight) => formatWeight(weight),
        sorter: (a, b) => utils.antdTableSorter(a, b, "totalWeight"),
      },
      {
        title: t("total_revenue"),
        dataIndex: "totalValue",
        render: (value) => <span>{CURRENCY + formatPrice(value)}</span>,
        sorter: (a, b) => utils.antdTableSorter(a, b, "totalValue"),
      },
      {
        title: "",
        render: (_, product) => (
          <div
            className="text-right"
            style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "3px" }}
          >
            <EllipsisDropdown menu={dropdownMenu(product)} />
          </div>
        ),
      },
    ],
    [dropdownMenu, showProductDetails, t],
  );

  // Table columns for product customers in drawer
  const customersColumns = useMemo(
    () => [
      {
        title: t("customer.name"),
        dataIndex: "customerName",
        sorter: (a, b) => utils.antdTableSorter(a, b, "customerName"),
      },
      {
        title: t("ordered_quantity"),
        dataIndex: "totalOrderedQuantity",
        sorter: (a, b) => utils.antdTableSorter(a, b, "totalOrderedQuantity"),
      },
      {
        title: t("delivered_quantity"),
        dataIndex: "totalDeliveredQuantity",
        sorter: (a, b) => utils.antdTableSorter(a, b, "totalDeliveredQuantity"),
      },
      {
        title: t("credited_quantity"),
        dataIndex: "totalCreditedQuantity",
        render: (qty) => (qty > 0 ? <span style={{ color: "#ff4d4f" }}>-{qty}</span> : "-"),
        sorter: (a, b) => utils.antdTableSorter(a, b, "totalCreditedQuantity"),
      },
      {
        title: t("total_weight"),
        dataIndex: "totalWeight",
        render: (weight) => formatWeight(weight),
        sorter: (a, b) => utils.antdTableSorter(a, b, "totalWeight"),
      },
      {
        title: t("total_value"),
        dataIndex: "totalValue",
        render: (value) => <span>{CURRENCY + formatPrice(value)}</span>,
        sorter: (a, b) => utils.antdTableSorter(a, b, "totalValue"),
      },
    ],
    [t],
  );

  const urlParams = getURLParamsString();
  const availabilityUrl = urlParams ? `${productsAvailabilityReport}?${urlParams}` : productsAvailabilityReport;

  return (
    <>
      <StyledCard
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <Link to={availabilityUrl}>
              <Button type="link">{`${t("view_products_availability")} →`}</Button>
            </Link>
            <Dropdown
              trigger={["click"]}
              open={categoryDropdownOpen}
              onOpenChange={handleCategoryDropdownOpenChange}
              dropdownRender={() => (
                <div
                  style={{
                    background: "#fff",
                    borderRadius: 8,
                    boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
                    border: "1px solid #f0f0f0",
                    paddingBottom: 3,
                  }}
                >
                  <div style={{ padding: 12, width: 320 }}>
                    <div style={{ maxHeight: 320, overflow: "auto", paddingRight: 6 }}>
                      <Checkbox.Group
                        style={{ width: "100%" }}
                        value={pendingSelectedCategories}
                        onChange={(values) => setPendingSelectedCategories(values)}
                      >
                        <Space direction="vertical" style={{ width: "100%" }}>
                          {categoryOptions.map((value) => (
                            <Checkbox key={value} value={value}>
                              {value}
                            </Checkbox>
                          ))}
                        </Space>
                      </Checkbox.Group>
                    </div>
                    <div
                      style={{
                        borderTop: "1px solid #f0f0f0",
                        paddingTop: 12,
                        display: "flex",
                        gap: 8,
                        justifyContent: "space-between",
                      }}
                    >
                      <Button
                        onClick={handleResetCategoryFilter}
                        disabled={!isCategoryFilterActive && pendingSelectedCategories.length === 0}
                      >
                        {t("reset")}
                      </Button>
                      <Button type="primary" onClick={handleConfirmCategoryFilter}>
                        OK
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            >
              <Button type={isCategoryFilterActive ? "primary" : "default"}>
                {t("category")} {isCategoryFilterActive ? `(${selectedCategories.length})` : ""} <FilterOutlined />
              </Button>
            </Dropdown>

            <Dropdown
              trigger={["click"]}
              open={productGroupDropdownOpen}
              onOpenChange={handleProductGroupDropdownOpenChange}
              dropdownRender={() => (
                <div
                  style={{
                    background: "#fff",
                    borderRadius: 8,
                    boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
                    border: "1px solid #f0f0f0",
                  }}
                >
                  <div style={{ padding: 12, width: 320 }}>
                    <div style={{ maxHeight: 320, overflow: "auto", paddingRight: 6 }}>
                      <Checkbox.Group
                        style={{ width: "100%" }}
                        value={pendingSelectedProductGroups}
                        onChange={(values) => setPendingSelectedProductGroups(values)}
                      >
                        <Space direction="vertical" style={{ width: "100%" }}>
                          {productGroupOptions.map((value) => (
                            <Checkbox key={value} value={value}>
                              {value}
                            </Checkbox>
                          ))}
                        </Space>
                      </Checkbox.Group>
                    </div>
                    <div
                      style={{
                        borderTop: "1px solid #f0f0f0",
                        paddingTop: 12,
                        display: "flex",
                        gap: 8,
                        justifyContent: "space-between",
                      }}
                    >
                      <Button
                        onClick={handleResetProductGroupFilter}
                        disabled={!isProductGroupFilterActive && pendingSelectedProductGroups.length === 0}
                      >
                        {t("reset")}
                      </Button>
                      <Button type="primary" onClick={handleConfirmProductGroupFilter}>
                        OK
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            >
              <Button type={isProductGroupFilterActive ? "primary" : "default"}>
                {t("product_group")} {isProductGroupFilterActive ? `(${selectedProductGroups.length})` : ""}{" "}
                <FilterOutlined />
              </Button>
            </Dropdown>
          </div>
        }
        extra={
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExportToExcel}
            disabled={!filteredProductsData || filteredProductsData.length === 0}
          >
            Excel
          </Button>
        }
      >
        <div style={{ marginBottom: 16 }}>
          <span className="text-muted">{dateRangeText}</span>
        </div>
        <ProductsTable columns={productsColumns} dataSource={filteredProductsData} loading={loading} />
      </StyledCard>

      <Drawer
        title={selectedProduct ? `${selectedProduct.productName} - ${t("customers_that_ordered_this_product")}` : ""}
        placement="right"
        onClose={closeDrawer}
        open={drawerVisible}
        width={800}
      >
        {selectedProduct && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <strong>{t("total_ordered_quantity")}: </strong>
              {selectedProduct.totalOrderedQuantity}
            </div>
            <div style={{ marginBottom: 16 }}>
              <strong>{t("total_delivered_quantity")}: </strong>
              {selectedProduct.totalDeliveredQuantity}
            </div>
            {selectedProduct.totalCreditedQuantity > 0 && (
              <div style={{ marginBottom: 16 }}>
                <strong>{t("total_credited_quantity")}: </strong>
                <span style={{ color: "#ff4d4f" }}>-{selectedProduct.totalCreditedQuantity}</span>
              </div>
            )}
            <div style={{ marginBottom: 16 }}>
              <strong>{t("packed_units")}: </strong>
              {selectedProduct.totalPackedQuantity || 0}
            </div>
            <div style={{ marginBottom: 16 }}>
              <strong>{t("total_weight")}: </strong>
              {formatWeight(selectedProduct.totalWeight)}
            </div>
            <div style={{ marginBottom: 16 }}>
              <strong>{t("total_revenue")}: </strong>
              {CURRENCY + formatPrice(selectedProduct.totalValue)}
            </div>
            <div style={{ marginBottom: 16 }}>
              <strong>{t("date_range")}: </strong>
              {dateRangeText}
            </div>
            <Table
              showSorterTooltip={false}
              columns={customersColumns}
              dataSource={selectedProductCustomers}
              rowKey="customerId"
              pagination={false}
              scroll={{ x: 400 }}
            />
          </div>
        )}
      </Drawer>
    </>
  );
};

const StyledCard = styled(Card)`
  .ant-card-head-wrapper {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .ant-card-head-title {
    flex: 1;
    min-width: 0;
  }

  .ant-card-extra {
    margin-left: auto;
    display: flex;
    align-items: center;
  }
`;

export default ProductsReport;
